<template>
  <portal to="modal">
    <section
      :class="[$style.wrapper, { [$style.scrollLock]: scrollLock }]"
      @click.stop="closeOverlay"
    >
      <div
        ref="modal"
        :class="modalClasses"
        :style="modalStyles"
        @click.stop
      >
        <header :class="$style.header">
          {{ title }}
          <button
            :class="$style.buttonClose"
            @click="close"
          >
            <img
              src="@/assets/images/icons/computer/screenshots/modal/close.svg"
              :alt="$t('global.toClose')"
            >
          </button>
        </header>
        <article :class="$style.article">
          <slot />
        </article>
        <footer
          v-if="$slots.footer"
          :class="[$style.footer, footerClass]"
        >
          <slot name="footer" />
        </footer>
      </div>
    </section>
  </portal>
</template>

<script>
  import { Portal } from 'portal-vue';
  import ScrollLock from '@/utils/scroll-lock';

  export default {
    components: {
      Portal,
    },
    props: {
      title: {
        type: String,
        default: '',
      },
      fullscreen: {
        type: Boolean,
        default: false,
      },
      scrollLock: {
        type: Boolean,
        default: false,
      },
      resizeModal: {
        type: Boolean,
        default: false,
      },
      storageKey: {
        type: String,
        default: 'modal-size',
      },
      footerClass: {
        type: String,
        default: '',
      },
      minWidth: {
        type: Number,
        default: 600,
      },
      minHeight: {
        type: Number,
        default: 300,
      },
    },
    data() {
      return {
        width: null,
        height: null,
        ro: null,
      };
    },
    computed: {
      modalStyles() {
        if (!this.resizeModal) {
          return {};
        }

        return {
          width: this.width ? `${this.width}px` : '',
          height: this.height ? `${this.height}px` : '',
          minWidth: `${this.minWidth}px`,
          minHeight: `${this.minHeight}px`,
        };
      },
      modalClasses() {
        return [
          this.$style.modal,
          {
            [this.$style.modalFullscreen]: this.fullscreen,
            [this.$style.resizeModal]: this.resizeModal,
          },
        ];
      },
    },
    mounted() {
      ScrollLock.enable();
      this.$nextTick(() => {
        this.restoreSize();
        this.initResizeObserver();
      });
    },
    beforeDestroy() {
      ScrollLock.disable();
      if (this.ro) {
        this.ro.disconnect();
      }
    },
    methods: {
      initResizeObserver() {
        if (!this.resizeModal) return;

        const el = this.$refs.modal;

        if (!el || typeof ResizeObserver === 'undefined') return;

        this.ro = new ResizeObserver(() => {
          const rect = el.getBoundingClientRect();

          const width = Math.max(Math.round(rect.width), this.minWidth);
          const height = Math.max(Math.round(rect.height), this.minHeight);

          if (width === this.width && height === this.height) {
            return;
          }

          this.width = width;
          this.height = height;

          this.saveSize();
        });

        this.ro.observe(el);
      },
      saveSize() {
        if (!this.width || !this.height) return;

        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            width: this.width,
            height: this.height,
          }),
        );
      },
      restoreSize() {
        if (!this.resizeModal) return;

        const saved = localStorage.getItem(this.storageKey);

        if (!saved) {
          this.width = this.minWidth;
          this.height = this.minHeight;
          return;
        }

        try {
          const { width, height } = JSON.parse(saved);

          this.width = Number.isFinite(width) ? Math.max(width, this.minWidth) : this.minWidth;

          this.height = Number.isFinite(height) ? Math.max(height, this.minHeight) : this.minHeight;
        } catch (e) {
          console.error(e);

          this.width = this.minWidth;
          this.height = this.minHeight;
        }
      },

      close() {
        this.$emit('close');
      },

      closeOverlay() {
        if (!this.resizeModal) {
          this.close();
        }
      },
    },
  };
</script>

<style lang="scss" module>
  .wrapper {
    position: fixed;
    z-index: 1001;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(25, 25, 25, 0.3);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: auto;
  }

  .scrollLock {
    overflow: visible;
  }

  .modal {
    width: 100%;
    background: white;
    margin: auto;
    display: flex;
    flex-direction: column;
  }

  .modalFullscreen {
    max-width: 100%;
    width: 100%;
    height: 100%;
  }

  .header {
    text-align: center;
    padding: 20px 30px;
    background: $white;
    box-shadow: rgba(218, 218, 218, 0.25);
    position: sticky;
    top: 0;
    z-index: 1001;
  }

  .buttonClose {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    border: 0;
    background: none;
    cursor: pointer;
    padding: 0;
  }

  .article {
    flex: 1;
    background: $light-gray;
    min-height: 0;
  }

  .footer {
    padding: 12px;
    background: $white;
  }

  .resizeModal {
    resize: both;
    overflow: auto;
    width: 600px;
    height: 300px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
  }
</style>
